import { z } from "zod";
import { DateValidation } from "../shared/date-validation";

export const BusinessUnitHardwareItemSchema = z.object({
  // Core identification
  Id: z.string().uuid(),
  Name: z.string().optional(),

  // Business Unit reference
  BusinessUnitId: z.string().uuid().optional(),
  BusinessUnitName: z.string().optional(),

  // Product information
  ProductId: z.string().uuid().optional(),
  ProductName: z.string().optional(),

  // Purchase reference
  PurchasedProductId: z.string().uuid().optional(),

  // Sales order information
  SalesOrderId: z.string().uuid().optional(),

  // Dates
  DateSold: DateValidation,
  DatePaid: DateValidation,
  DateShipped: DateValidation,
  DateDelivered: DateValidation,
  DateInstalled: DateValidation,
  DateStarted: DateValidation,
  DateStopped: DateValidation,
  DateCancelled: DateValidation,
  Created: DateValidation,
  ExpectedDeliveryDate: DateValidation,

  // Status information
  StateCode: z.number().optional(),
  StatusCode: z.number().optional(),
  ProductStatusCode: z.number().optional(),
  ProductStatusName: z.string().optional(),

  // Delivery tracking
  TrackingNumber: z.string().optional(),
  DeliveryStatus: z.string().optional(),
  DeliveryStatusName: z.string().optional(),
  DeliveryStatusMessage: z.string().optional(),
  DeliveryStatusLocation: z.string().optional(),
  DeliveryStatusDate: DateValidation,
  DeliveryCourier: z.string().optional(),

  // Shipping information
  ShipmentType: z.string().optional(),
  ShippingMethodCode: z.number().optional(),
  ShipToLine1: z.string().optional(),
  ShipToLine2: z.string().optional(),
  ShipToCity: z.string().optional(),
  ShipToStateOrProvince: z.string().optional(),
  ShipToPostalCode: z.string().optional(),
  ShipToCountry: z.string().optional(),
  ShippingFreightTermsCode: z.number().optional(),

  // Quantity and inventory
  Quantity: z.number().optional(),
  QuantityBackOrdered: z.number().optional(),
  QuantityCancelled: z.number().optional(),
  QuantityShipped: z.number().optional(),

  // Hardware specific
  SerialNumber: z.string().optional(),
  Installed: z.boolean().optional(),

  // License information (for software hardware)
  LicenseKey: z.string().optional(),
  LicenseKeyStatus: z.string().optional(),

  // Contact and activation
  ContactId: z.string().uuid().optional(),
  ActivatedById: z.string().uuid().optional(),
  ActivatedByName: z.string().optional(),
  InstallerId: z.string().uuid().optional(),

  // Device reference
  DeviceId: z.string().uuid().optional(),

  // Financial information
  IsPaid: z.boolean().optional(),
  PaidStatusCode: z.number().optional(),
  InvoiceCreated: z.boolean().optional(),
  InvoiceSent: z.boolean().optional(),
  TaxAmount: z.number().optional(),
  BaseAmount: z.number().optional(),
  ExtendedAmount: z.number().optional(),
  ListPrice: z.number().optional(),
  ManualDiscountAmount: z.number().optional(),
  PricePerUnitAmount: z.number().optional(),
  SalesPrice: z.number().optional(),

  // Additional metadata
  Description: z.string().optional(),
  Unit: z.string().optional(),
  PackageCount: z.number().optional(),
  LineItemNumber: z.number().optional(),
  LineTypeCode: z.number().optional(),
  SalesChannelCode: z.number().optional(),

  // Trial and portal flags
  IsTrialOffer: z.boolean().optional(),
  IsPortal: z.boolean().optional(),
  IsDeleted: z.boolean().optional(),

  // Cancellation
  ReasonForCancellationCode: z.number().optional(),
  ReasonForCancellationNotes: z.string().optional(),

  // References to other entities
  CurrencyId: z.string().uuid().optional(),
  UnitOfMeasureId: z.string().uuid().optional(),
  ServiceLicenseAgreementId: z.string().uuid().optional(),
  PriceListId: z.string().uuid().optional(),
  InvoiceLineItemId: z.string().uuid().optional(),

  // Cloud integration
  PetrosoftCloudId: z.string().optional(),
});

export type BusinessUnitHardwareItemType = z.infer<
  typeof BusinessUnitHardwareItemSchema
>;
